<script setup>
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import TheTextarea from '@shared-components/ui/form/TheTextarea.vue'
import {computed, defineModel, defineProps, defineExpose, ref} from 'vue'
import {getCurrencySign, removeObjectFromArray} from '@shared-utils/helpers'
import {useCompanyBillingStore} from '@/views/finance/CompanyBillingStore'

const modelValue = defineModel({required: true})
const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  invoiceType: {
    type: [String, undefined],
    default: 'estimate',
  },
  recommendedTopupAmount: {
    type: [Number, undefined],
    default: 0,
  },
  currency: {
    type: String,
    default: getCurrencySign('USD'),
  },
  canDeleteExistingServices: {
    type: Boolean,
    default: true,
  },
})

const {getQuickBooksServices} = useCompanyBillingStore()

// State
const isServicesValid = ref(true)
const validationError = ref()

// Services offered in the dropdown come from the backend `quick-books/services` endpoint (for the
// active prod/sandbox environment). Line-item behaviour that is specific to a few services is keyed
// to their ids, resolved from the same response by their environment-independent name.
const SERVICE_NAMES = {
  advertisingServices: 'Advertising Services',
  couponIncome: 'Coupon Spend Deduction',
  financeFee: 'Finance Fee',
  creativeStudio: 'Creative Studio',
  metaOtherServices: 'Meta Other Services',
}
const services = ref([])
const productIds = ref({})

const loadServices = async () => {
  services.value = await getQuickBooksServices()
  const idByName = Object.fromEntries(services.value.map(service => [service.name, service.id]))

  productIds.value = Object.fromEntries(Object.entries(SERVICE_NAMES).map(([key, name]) => [key, idByName[name]]))
}
const servicesReady = loadServices()

const defaultLineItem = {
  service_id: '',
  service_description: '',
  price: null,
}
const advertisingLineItemDescription =
  'Advertising Accounts up on the Client’s request Support related to provided Advertising Accounts ' +
  'Landing pages and Creatives Moderation.\n' +
  "Ads Creation upon the Client's request."

// Computed
const productOptions = computed(() =>
  services.value
    // Finance Fee applies only to invoices, not estimates/proformas.
    .filter(service => props.invoiceType === 'invoice' || service.id !== productIds.value.financeFee)
    .map(service => ({label: service.name, value: service.id})),
)
const canAddLineItems = computed(() => modelValue.value.length < productOptions.value.length)

// Methods
const initLineItems = async () => {
  await servicesReady

  modelValue.value.push({
    service_id: productIds.value.advertisingServices,
    service_description: advertisingLineItemDescription,
    price: Math.trunc(props.recommendedTopupAmount) || null,
  })
}
const addNewLineItem = () => {
  if (canAddLineItems.value) {
    modelValue.value.push({
      ...cloneSafe(defaultLineItem),
      price: null,
      _isNew: true,
    })
  }
}

const setServiceDescription = service_id => {
  const mapper = {
    [productIds.value.advertisingServices]: advertisingLineItemDescription,
    [productIds.value.couponIncome]: '',
    [productIds.value.financeFee]: 'Commission fee of 0.5% based on the actual usage of the line of credit',
    [productIds.value.creativeStudio]: '',
    [productIds.value.metaOtherServices]: '',
  }

  return mapper[service_id] || ''
}

const onLineItemServiceChange = (service_id, index) => {
  modelValue.value[index].service_description = setServiceDescription(service_id)
  modelValue.value[index].price = null
}

const getProductOptions = currentServiceId => {
  const selectedIds = modelValue.value
    .map(item => Number(item.service_id))
    .filter(id => id !== Number(currentServiceId))

  return productOptions.value.filter(
    option =>
      // Ids resolve asynchronously from the backend; skip options not yet (or not) resolved.
      option.value != null && (!selectedIds.includes(option.value) || option.value === Number(currentServiceId)),
  )
}

const emitValidation = isValid => {
  if (!isValid) {
    validationError.value = {
      validation: false,
      message: 'The total invoice amount needs to be more than zero.',
    }
    isServicesValid.value = false

    return
  }

  validationError.value = ''
  isServicesValid.value = true
}

const formatPrice = serviceId => {
  if (productIds.value.couponIncome === serviceId) {
    const couponItem = modelValue.value.find(item => item.service_id === serviceId)

    if (couponItem.price > 0) {
      couponItem.price = -couponItem.price
    }
  }
}

const onPriceChange = serviceId => {
  formatPrice(serviceId)

  const totalPrice = modelValue.value.reduce((sum, item) => sum + (item?.price || 0), 0)

  const isValid = totalPrice > 0

  emitValidation(isValid)
}

const onPriceKeydown = (e, item) => {
  if (item.service_id !== productIds.value.couponIncome && e.key === '-') {
    e.preventDefault()
  }
}

const isCanRemoveItem = item => {
  if (props.canDeleteExistingServices) {
    return true
  }

  if (item._isNew) {
    return true
  }
}

defineExpose({initLineItems, productIds, isServicesValid, validationError})
</script>

<template>
  <div>
    <h5 class="invoice-services-title custom fs-5 mb-5">Services</h5>
    <div class="invoice-services-wrapper">
      <div class="invoice-services-head">
        <div class="d-flex custom gap-4">
          <div class="w-230-px flex-shrink-0">
            <label>Activity</label>
          </div>
          <div class="w-150-px flex-shrink-0">
            <label>Amount</label>
          </div>
          <div class="flex-grow-1">
            <label>Description</label>
          </div>
        </div>
      </div>
      <div class="invoice-services-body">
        <div class="d-flex custom gap-4 mb-4" v-for="(item, index) in modelValue" :key="index">
          <div class="w-230-px flex-shrink-0">
            <the-select
              v-model="item.service_id"
              :options="getProductOptions(item.service_id)"
              :allow_clear="false"
              :can_deselect="false"
              @change="onLineItemServiceChange($event, index)"
              placeholder="Product/Service"
              class="w-100"
              :preselect="false"
              :disabled="disabled"
              required
            />
          </div>
          <div class="w-150-px flex-shrink-0">
            <div class="input-group">
              <div class="input-group-text">{{ currency }}</div>
              <input
                v-model.number="item.price"
                class="form-control"
                type="number"
                step="0.01"
                :min="item.service_id === productIds.couponIncome ? undefined : 0"
                :disabled="disabled"
                @keydown="onPriceKeydown($event, item)"
                @input="onPriceChange(item.service_id)"
                required
              />
            </div>
          </div>
          <div class="flex-grow-1">
            <the-textarea
              v-model="item.service_description"
              class="form-control no-resize"
              placeholder="Services Provided by Point2Web as Agreed"
              :disabled="disabled"
              :id="`service_description_${index}`"
              rows="1"
              required
            />
          </div>
          <div v-if="isCanRemoveItem(item) && modelValue.length > 1" class="flex-shrink-0">
            <div class="input-height">
              <button
                class="form-icon-btn"
                @click="removeObjectFromArray(index, modelValue)"
                :disabled="disabled"
                type="button"
              >
                <i class="fa-light fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
        <span
          v-if="canAddLineItems"
          v-tooltip="disabled ? 'Can\'t add new items since invoice already created in QuickBooks' : ''"
          class="d-inline-block"
        >
          <button
            @click="addNewLineItem"
            class="btn btn-secondary d-flex align-items-center gap-1"
            type="button"
            :disabled="disabled"
          >
            <img src="~@shared-images/icons/button/plus.svg" alt="plus" /> <span>Add item</span>
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.invoice-services-title {
  font-weight: 600;
  line-height: 1.4;
  color: $black;
}
</style>
